import type { FC } from 'react';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  CloseIcon,
  Collapse,
  Drawer,
  ExpandMoreIcon,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
} from '@/shared/ui/mui';
import useAuth from '@/features/auth/hooks/useAuth';
import { featureMenuSections, getFeaturePath } from '@/features/landing/unified/data/featureCatalog';
import { logger } from '@/shared/utils/logger';

interface MobileLandingDrawerProps {
  open: boolean;
  onClose: () => void;
  isHomePage: boolean;
  isProviderView: boolean;
}

export const MobileLandingDrawer: FC<MobileLandingDrawerProps> = ({
  open,
  onClose,
  isHomePage,
  isProviderView,
}) => {
  const theme = useTheme();
  const { login } = useAuth();
  const [isFeaturesExpanded, setIsFeaturesExpanded] = useState(false);

  const handleClose = () => {
    onClose();
    setIsFeaturesExpanded(false);
  };

  const handleLogin = async () => {
    handleClose();
    try {
      await login(isProviderView ? 'provider' : undefined);
    } catch (error) {
      logger.error('Login failed:', error);
    }
  };

  const scrollToAnchor = (anchor: string) => {
    const el = document.querySelector(anchor);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          backgroundColor: theme.palette.common.white,
          pt: 'calc(var(--promo-offset, 0px) + 56px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={handleClose} aria-label="Close navigation menu">
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ px: 1, flexGrow: 1 }}>
        {isHomePage && (
          <ListItemButton
            component="a"
            href="#how-it-works"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              handleClose();
              scrollToAnchor('#how-it-works');
            }}
            sx={{
              borderRadius: 1,
              py: 1.5,
              bgcolor: theme.palette.action.hover,
              mb: 0.5,
            }}
          >
            <ListItemText
              primary="How It Works"
              slotProps={{ primary: { fontWeight: 600, fontSize: '1rem' } }}
            />
          </ListItemButton>
        )}

        {isProviderView && (
          <>
            <ListItemButton
              onClick={() => setIsFeaturesExpanded(prev => !prev)}
              sx={{ borderRadius: 1, py: 1.5 }}
            >
              <ListItemText primary="Features" slotProps={{ primary: { fontWeight: 600, fontSize: '1rem' } }} />
              <ExpandMoreIcon
                sx={{
                  transform: isFeaturesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </ListItemButton>
            <Collapse in={isFeaturesExpanded}>
              <List disablePadding>
                {featureMenuSections.map(section => (
                  <React.Fragment key={section.id}>
                    <ListItemText
                      sx={{ pl: 3, pt: 1 }}
                      primary={section.title}
                      slotProps={{ primary: {
                        variant: 'caption',
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                      } }}
                    />
                    {section.features.map(item => (
                      <ListItemButton
                        key={item.slug}
                        component={RouterLink}
                        to={getFeaturePath(item.slug)}
                        onClick={handleClose}
                        sx={{ pl: 4, borderRadius: 1 }}
                      >
                        <ListItemText
                          primary={item.name}
                          slotProps={{ primary: { fontSize: '0.9rem' } }}
                        />
                      </ListItemButton>
                    ))}
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {isProviderView && isHomePage && (
          <ListItemButton
            component="a"
            href="#pricing"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              handleClose();
              scrollToAnchor('#pricing');
            }}
            sx={{ borderRadius: 1, py: 1.5 }}
          >
            <ListItemText primary="Pricing" slotProps={{ primary: { fontWeight: 600, fontSize: '1rem' } }} />
          </ListItemButton>
        )}

        <ListItemButton
          component={RouterLink}
          to="/contact"
          onClick={handleClose}
          sx={{ borderRadius: 1, py: 1.5 }}
        >
          <ListItemText primary="Support" slotProps={{ primary: { fontWeight: 600, fontSize: '1rem' } }} />
        </ListItemButton>
      </List>

      {/* Login button at bottom of drawer */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: theme.palette.grey[200] }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleLogin}
          sx={{
            py: 1.25,
            fontWeight: 600,
            fontSize: '0.95rem',
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: theme.palette.primary.ultraLight,
            },
          }}
        >
          Log in
        </Button>
      </Box>
    </Drawer>
  );
};
